<template>
  <div id="articleDetailsContainer">
    <h1 id="articleTitle">{{ title }}</h1>
    <div v-if="articleIsText">
      <div>
        <p v-for="lineData in content" :key="lineData.id">
          <span
            v-for="element in lineData.serialized_data.elements"
            :key="element.id"
            :class="{
              newLink: isNewLink(element, lineData.id),
              linkToArticle: element.is_link,
              linkAlike: element.is_link,
            }"
          >
            <span
              v-for="(word, wordIndex) in element.content.split(' ')"
              :key="`${lineData.id}-${element.indexes[wordIndex]}`"
              :id="`${lineData.id}-${element.indexes[wordIndex]}`"
              v-on="
                isMobile
                  ? {
                      click: () =>
                        handleClickOnMobile(
                          word,
                          element.indexes[wordIndex],
                          lineData.id,
                          element.is_link,
                          element.link_to_url
                        ),
                    }
                  : {
                      mousedown: () =>
                        setSelectionStart(element.indexes[wordIndex]),
                      mouseup: () =>
                        setSelectionEnd(
                          element.indexes[wordIndex],
                          lineData.id
                        ),
                      click: () =>
                        handleClick(element.is_link, element.link_to_url),
                    }
              "
            >
              {{ word }}
            </span>
          </span>
        </p>
      </div>
      <div v-if="showArticleForm">
        <article-create
          @load_new_article_data="reloadCurrentArticle"
          @cancel_create_article="cancelCreateArticle"
          @create_article_form_ready="dispatchScrollToArticleForm"
          :selectedWords="selectedWordsAsString"
          :selectedWordsIndexes="selectedWordsIndexes"
          :selectedParagraphId="selectedParagraphId"
          :createFromArticle="articleId"
        ></article-create>
      </div>
    </div>
    <div v-if="articleIsFile"><img :src="media" :alt="title" /></div>
  </div>
</template>

<script>
import rangy from "rangy";
import ArticleCreate from "./create.vue";

export default {
  props: {
    title: String,
    articleId: Number,
    editable: Boolean,
    media: String,
    content: Array,
    newLinkParagraphId: Number,
    newLinkWordsIndexes: Array,
  },
  data() {
    return {
      articleFileName: null,
      selectedWordsIndexes: [],
      selectedWords: [],
      selectedParagraphId: null,
      scrollToArticleTimeout: null,
      tapped: null,
      scrollOptions: {
        container: "#articleDetailsContainer",
        easing: "ease-in",
        offset: -60,
        force: true,
        cancelable: true,
        onStart: function () {},
        onDone: function () {},
        onCancel: function () {},
        x: false,
        y: true,
      },
    };
  },
  watch: {
    title() {
      this.cancelCreateArticle();
    },
  },
  computed: {
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    },
    articleIsText() {
      return this.content !== null && this.content.length !== 0;
    },
    articleIsFile() {
      return this.media !== null;
    },
    selectedWordsAsString() {
      return this.selectedWords.join(" ");
    },
    showArticleForm() {
      return this.selectedWords.length > 0;
    },
  },
  methods: {
    handleClickOnMobile(
      word,
      wordIndex,
      selectedParagraphId,
      isLink,
      linkToUrl
    ) {
      if (this.tapped === null) {
        this.tapped = setTimeout(() => {
          this.tapped = null;
          this.handleClick(isLink, linkToUrl);
        }, 400);
      } else {
        clearTimeout(this.tapped);
        this.tapped = null;
        this.addSelectedWord(word, wordIndex, selectedParagraphId);
      }
    },
    handleClick(isLink, linkToUrl) {
      if (isLink) {
        this.$router.push(linkToUrl);
      }
    },
    isNewLink(element, paragraphId) {
      let paragraphsMatch = paragraphId === this.newLinkParagraphId;
      let indexesMatch =
        JSON.stringify(element.indexes) ===
        JSON.stringify(this.newLinkWordsIndexes);
      return paragraphsMatch && indexesMatch;
    },
    dispatchScrollToArticleForm() {
      this.scrollToArticleTimeout = setTimeout(
        this.scrollToArticleCreateForm,
        2000
      );
    },
    scrollToArticleCreateForm() {
      this.$scrollTo("#createArticleFormContainer", 500, this.scrollOptions);
    },
    assertEdit() {
      if (!this.editable) {
        this.$store.dispatch("sendNewPopupMessage", {
          type: "pop-up-edit-disabled",
        });
      }
    },
    findFirstSelectedWordIndex(selectionAnchorOffset, element) {
      let firstWordIndexInElement = element.indexes[0];
      let subArticleData = element.content.substring(0, selectionAnchorOffset);
      return firstWordIndexInElement + subArticleData.split(" ").length - 1;
    },
    setSelectionStart(firstSelectedWordIndex) {
      if (this.editable) {
        this.selectedWordsIndexes = [firstSelectedWordIndex];
      }
    },
    setSelectionEnd(lastSelectedWordIndex, selectedParagraphId) {
      let selection = rangy.getSelection();
      if (selection.anchorOffset !== selection.focusOffset) {
        if (this.editable) {
          this.selectedWords = selection.toString().trim().split(" ");
          let isBackwards = selection.isBackwards();
          let firstSelectedWordIndex = this.selectedWordsIndexes[0];
          let selectionStartIndex = isBackwards
            ? lastSelectedWordIndex
            : firstSelectedWordIndex;
          let selectionEndIndex = isBackwards
            ? firstSelectedWordIndex
            : lastSelectedWordIndex;
          this.selectedWordsIndexes = [
            ...Array(selectionEndIndex - selectionStartIndex + 1).keys(),
          ].map((i) => i + selectionStartIndex);
          this.selectedParagraphId = selectedParagraphId;
        } else {
          this.assertEdit();
        }
      }
    },
    addSelectedWord(word, wordIndex, selectedParagraphId) {
      if (!this.editable) {
        this.assertEdit();
      } else {
        if (this.scrollToArticleTimeout !== null) {
          clearTimeout(this.scrollToArticleTimeout);
          this.scrollToArticleTimeout = null;
          this.dispatchScrollToArticleForm();
        }
        this.selectedParagraphId = selectedParagraphId;
        if (this.selectedWordsIndexes.length === 0) {
          this.selectedWordsIndexes.push(wordIndex);
          this.selectedWords.push(word);
          document
            .getElementById(`${selectedParagraphId}-${wordIndex}`)
            .classList.add("selectedWord");
        } else {
          if (wordIndex === Number(this.selectedWordsIndexes.slice(-1)) + 1) {
            this.selectedWordsIndexes.push(wordIndex);
            this.selectedWords.push(word);
            document
              .getElementById(`${selectedParagraphId}-${wordIndex}`)
              .classList.add("selectedWord");
          } else if (wordIndex === this.selectedWordsIndexes[0] - 1) {
            this.selectedWordsIndexes.unshift(wordIndex);
            this.selectedWords.unshift(word);
            document
              .getElementById(`${selectedParagraphId}-${wordIndex}`)
              .classList.add("selectedWord");
          } else {
            this.$store.dispatch("sendNewPopupMessage", {
              type: "pop-up-assert-following-words",
            });
          }
        }
      }
    },
    reloadCurrentArticle() {
      let newLinkParagraphId = this.selectedParagraphId;
      let newLinkWordsIndexes = this.selectedWordsIndexes;
      this.cancelCreateArticle();
      this.$emit("reload_current_article", {
        newLinkParagraphId: newLinkParagraphId,
        newLinkWordsIndexes: newLinkWordsIndexes,
      });
    },
    cancelCreateArticle() {
      this.selectedWordsIndexes.forEach((element) => {
        let selectedWord = document.getElementById(
          `${this.selectedParagraphId}-${element}`
        );
        if (selectedWord !== null) {
          selectedWord.classList.remove("selectedWord");
        }
      });
      this.scrollToArticleTimout = null;
      this.selectedWordsIndexes = [];
      this.selectedWords = [];
      this.selectedParagraphId = null;
      rangy.getSelection().removeAllRanges();
      this.$scrollTo("#articleTitle", 500, this.scrollOptions);
    },
  },
  components: {
    "article-create": ArticleCreate,
  },
};
</script>

<style scoped>
h1 {
  padding-top: 10%;
  padding-bottom: 5%;
  font-family: "Calistoga Regular", sans-serif;
  font-size: 28px;
  margin: 0;
}

p {
  font-size: 16px;
  color: #000000;
  margin-top: 24px;
  margin-bottom: 24px;
  line-height: 24px;
}

.linkToArticle {
  font-size: 16px;
  color: #0094d2;
  text-decoration: none;
}

.linkToArticle:hover {
  text-decoration: underline;
}

.newLink {
  color: #ec783e !important;
}

#articleDetailsContainer {
  height: 90%;
  overflow-y: auto;
  margin-top: 10%;
  padding-bottom: 5%;
}

.selectedWord {
  background-color: #0094d2;
  color: black;
}

img {
  width: 100%;
}

@media (max-width: 1000px) {
  h1 {
    padding: 0;
  }

  #articleDetailsContainer {
    margin-top: 5%;
    height: 95%;
  }
}
</style>
